import { Box, Paper, Typography } from '@mui/material';
import type { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { GridRow, type GridRowParams, type GridRowProps } from '@mui/x-data-grid-pro';
import { animated } from '@react-spring/web';
import { createContext, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReadOnlyDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import { useLayout } from '../../context';
import type { LayoutList__Zone as Zone } from '../../queries/list.generated';
import { useReorder } from '../lib';
import { ZonesContainer, ZonesHeader } from '../lib/styles';
import { AddZoneButton } from './add-zone-button';
import { EditZoneDialog } from './edit-zone-dialog';

// The springs reach the row slot through context so the slot component is the same object on
// every render: a new row component remounts every row and drops keyboard focus mid-reorder.
const SpringsContext = createContext<ReturnType<typeof useReorder>['springs']>([]);

const AnimatedRow = (props: GridRowProps) => {
  const springs = useContext(SpringsContext);
  return (
    <animated.div style={springs.at(props.index)}>
      <GridRow {...props} style={{ position: 'absolute' }} />
    </animated.div>
  );
};

const NoResizeIcon = () => null;

export const LayoutZones = () => {
  const { t } = useTranslation();
  const layout = useLayout();
  const zones = layout.zones;
  const [currentZone, setCurrentZone] = useState<Zone>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const editZone = (zone: Zone) => {
    setCurrentZone(zone);
    setEditDialogOpen(true);
  };

  const { columns, confirmDeleteProps, showColumns, springs, zoneToDelete } = useReorder(
    layout.id,
    zones,
    editZone,
  );

  return (
    <Paper sx={{ mt: 2 }}>
      <ZonesHeader>
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 2 }}>
          {layout.zones.length > 0 && ( // Don't show the skeleton if there are no zones, looks dumb
            <Box sx={{ width: '150px' }}>
              <LayoutSkeleton layout={layout} />
            </Box>
          )}
          <Box>
            <Typography variant="h4">{t('pages.layouts.detail.sections.zones')}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {t('pages.layouts.detail.zones_description')}
            </Typography>
          </Box>
        </Box>
        <Box>
          <AddZoneButton
            disabled={!layout.canAddZone.value}
            title={layout.canAddZone.reasons?.fullMessages.join(',')}
          />
        </Box>
      </ZonesHeader>
      <ZonesContainer>
        <SpringsContext.Provider value={springs}>
          <ReadOnlyDataGrid
            columns={columns}
            columnVisibilityModel={showColumns}
            getRowClassName={({ row }: GridRowClassNameParams<Zone>) =>
              !row.canUpdate.value ? 'readonly' : ''
            }
            onRowClick={({ row }: GridRowParams<Zone>) => {
              row.canUpdate.value ? editZone(row) : '';
            }}
            hideFooter
            rows={zones}
            slots={{
              columnResizeIcon: NoResizeIcon,
              row: AnimatedRow,
              noRowsOverlay: () => {
                return (
                  <Box height="100%" display="flex" justifyContent="center" alignItems="center">
                    {t('pages.layouts.detail.no_zones')}
                  </Box>
                );
              },
            }}
          />
          {currentZone && (
            <EditZoneDialog
              close={() => setEditDialogOpen(false)}
              open={editDialogOpen}
              zone={currentZone}
            />
          )}
          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('pages.layouts.detail.delete_zone_dialog.confirm')}
            cancel={t('common.dialogs.cancel')}
            deleteConfirm
            prompt={
              <Trans
                i18nKey="pages.layouts.detail.delete_zone_dialog.prompt"
                values={{ zoneName: zoneToDelete?.name || '' }}
                components={{ b: <strong /> }}
              />
            }
            title={t('pages.layouts.detail.delete_zone_dialog.title')}
          />
        </SpringsContext.Provider>
      </ZonesContainer>
    </Paper>
  );
};
